<!-- eslint-disable perfectionist/sort-modules -->
<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import {
  computed, onMounted, ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { toast } from 'vue3-toastify';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import { campaignManagementService } from '~/services/campaign-management-api/campaign-management-api';
import getErrorMessage from '~/utils/getErrorMessage';

import type { CampaignDetail, DetailKpi, PartnerPerformance } from '../types';

import CampaignDetailKpiCards from '../components/CampaignDetailKpiCards.vue';
import CampaignPartnerTable from '../components/CampaignPartnerTable.vue';
import CampaignStatusBadge from '../components/CampaignStatusBadge.vue';
import { mapCampaignDetailResponse } from '../mappers/campaignMappers';
import { mapCampaignTargetsResponse } from '../mappers/partnerMappers';
import { CAMPAIGN_TARGETS_PAGE_SIZE } from '../constants';
import { STATUS_LABELS } from '../utils';

const route = useRoute();

const isLoadingCampaign = ref(true);
const isLoadingPartners = ref(true);
const hasCampaignError = ref(false);
const hasPartnersError = ref(false);
const campaign = ref<CampaignDetail | null>(null);
const detailKpis = ref<DetailKpi[]>([]);
const partners = ref<PartnerPerformance[]>([]);

const campaignId = computed(() => String(route.query.id ?? ''));

const subtitle = computed(() => {
  if (!campaign.value) return '';

  const parts = [
    campaign.value.code,
    `criada por ${campaign.value.createdBy} em ${campaign.value.createdAt}`,
  ];

  if (campaign.value.activatedAt) {
    parts.push(`ativa desde ${campaign.value.activatedAt}`);
  }

  return parts.join(' · ');
});

const statusLabel = computed(() => {
  if (!campaign.value) return '';

  if (campaign.value.status === 'active' && campaign.value.daysActive !== null) {
    return `${STATUS_LABELS[campaign.value.status]} há ${campaign.value.daysActive} dias`;
  }

  return STATUS_LABELS[campaign.value.status];
});

async function loadCampaign() {
  if (!campaignId.value) {
    hasCampaignError.value = true;
    isLoadingCampaign.value = false;
    return;
  }

  isLoadingCampaign.value = true;
  hasCampaignError.value = false;

  try {
    const data = await campaignManagementService.getCampaign(campaignId.value);
    const mapped = mapCampaignDetailResponse(data);

    campaign.value = mapped.campaign;
    detailKpis.value = mapped.kpis;
  } catch (error) {
    hasCampaignError.value = true;
    campaign.value = null;
    detailKpis.value = [];
    toast.error(getErrorMessage(error));
  } finally {
    isLoadingCampaign.value = false;
  }
}

async function loadPartners() {
  if (!campaignId.value) {
    hasPartnersError.value = true;
    isLoadingPartners.value = false;
    return;
  }

  isLoadingPartners.value = true;
  hasPartnersError.value = false;

  try {
    const data = await campaignManagementService.listCampaignTargets(campaignId.value, {
      page: 1,
      page_size: CAMPAIGN_TARGETS_PAGE_SIZE,
    });

    partners.value = mapCampaignTargetsResponse(data).partners;
  } catch {
    hasPartnersError.value = true;
    partners.value = [];
  } finally {
    isLoadingPartners.value = false;
  }
}

async function loadPage() {
  if (!campaignId.value) {
    hasCampaignError.value = true;
    hasPartnersError.value = true;
    isLoadingCampaign.value = false;
    isLoadingPartners.value = false;
    return;
  }

  await Promise.all([loadCampaign(), loadPartners()]);
}

watch(campaignId, () => {
  loadPage();
});

onMounted(() => {
  document.title = 'Detalhe da Campanha';
  loadPage();
});
</script>

<template>
  <DashboardLayout>
    <template #header-left>
      <RouterLink to="/revalida-campaigns" class="detail-breadcrumb">
        ← Campanhas
      </RouterLink>
      <h2 class="page-title" style="margin-top: 6px;">
        <template v-if="isLoadingCampaign">
          <span class="placeholder-glow">
            <span class="placeholder col-5" style="height: 22px; display: inline-block;" />
          </span>
        </template>
        <template v-else-if="campaign">{{ campaign.name }}</template>
        <template v-else>Campanha não encontrada</template>
      </h2>
      <p v-if="!isLoadingCampaign && campaign" class="page-subtitle">
        {{ subtitle }}
      </p>
    </template>

    <template #actions>
      <div v-if="campaign && !isLoadingCampaign" class="d-flex align-items-center gap-2">
        <CampaignStatusBadge
          size="md"
          :status="campaign.status"
          :label="statusLabel"
        />
      </div>
    </template>

    <div v-if="hasCampaignError && !isLoadingCampaign" class="row row-cards">
      <div class="col-12">
        <div class="error-state">
          Não foi possível carregar os dados da campanha.
        </div>
      </div>
    </div>

    <div v-else class="row row-cards">
      <CampaignDetailKpiCards :kpis="detailKpis" :is-loading="isLoadingCampaign" />

      <CampaignPartnerTable
        :partners="partners"
        :is-loading="isLoadingPartners"
        :has-error="hasPartnersError"
      />
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
.detail-breadcrumb {
  display: inline-block;
  font-size: 12px;
  color: #00B569;
  font-weight: 600;
  margin-bottom: 2px;
  text-decoration: none;

  &:hover { text-decoration: underline; }
}

.page-title {
  font-size: 22px;
  font-weight: 700;
  color: #111;
  margin: 0;
}

.page-subtitle {
  font-size: 13px;
  color: #6B7280;
  margin-top: 4px;
  margin-bottom: 0;
}

.error-state {
  background: #fff;
  border: 1px solid #E4E6EB;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  color: #6B7280;
  font-size: 14px;
}
</style>
